import { FeatureFlagName, FeatureFlagTypeMap } from '../App/app.api'
import { ROUTE_PATHS_PREFIX } from '../page.constants'

export const PROPERTY_FILE_IMPLEMENTATION = 'PROPERTY_FILE'
export const LAUNCHDARKLY_IMPLEMENTATION = 'LAUNCH_DARKLY'
export const UNLOADED_IMPLEMENTATION = 'UNLOADED'

interface FEATURE_FLAGS_PROPS {
  ANALYTICS_ENABLE_INTERACTION_SUMMARIZATION: FeatureFlagName,
  ANALYTICS_ENABLE_ADVANCED_SENTIMENT: FeatureFlagName,
  ANALYTICS_ENABLE_SMART_TAG: FeatureFlagName,
  ANALYTICS_DISABLE_SENTIMENT: FeatureFlagName,
  ANALYTICS_DISABLE_BULK_CONTACT_EXPORT: FeatureFlagName,
  ANALYTICS_DISABLE_PHRASE_MANAGER_SUGGESTIONS: FeatureFlagName,
  ANALYTICS_ALLOW_PHRASE_IN_MULTIPLE_LANGUAGES: FeatureFlagName,
  ANALYTICS_ENABLE_TRANSCRIPTION_SEARCH: FeatureFlagName,
  IAM_INTEGRATION: FeatureFlagName,
  IAM_FORGE_ROCK_USER_SYNC: FeatureFlagName,
  IAM_ALLOW_MFA_CONFIGURATION: FeatureFlagName,
  CDM_DATA_LIBRARY: FeatureFlagName,
  CDM_DATA_TASK_MANAGEMENT: FeatureFlagName,
  CDM_DATA_CONNECTOR_MANAGEMENT: FeatureFlagName,
  CDM_APP_SETTINGS: FeatureFlagName,
  CDM_SHARING_GROUP_ADMIN: FeatureFlagName,
  GENESYS_CONFIG: FeatureFlagName,
  FEDRAMP_LOGIN_BANNER: FeatureFlagName,
  CDM_USER_ADMIN: FeatureFlagName,
  BI_QS_EMBEDDING: FeatureFlagName,
  BI_QS_EMBEDDING_LIB_URL: FeatureFlagName,
  BI_QS_EMBEDDING_AUTH_URL: FeatureFlagName,
  BI_QS_EMBEDDING_BACKEND_URL: FeatureFlagName,
  BI_QS_DASHBOARDS_URL: FeatureFlagName,
  QM_BULK_CONTACT_OPERATIONS_UPDATE_METADATA: FeatureFlagName,
  QM_MULTIEVAL_FORM: FeatureFlagName,
  QM_CONTACT_GOAL_ADMIN: FeatureFlagName,
  QM_CONTACT_GOALS_QUEUE: FeatureFlagName,
  USE_SYSTEM_METADATA_FIELDS: FeatureFlagName,
  ENABLE_SHARED_MEDIA_DIRECTORY: FeatureFlagName,
  USE_LANDINGPAGE_SERVICE: FeatureFlagName,
  LANDINGPAGE_SERVICE_PATH: FeatureFlagName,
  LANDINGPAGE_TIMEOUT: FeatureFlagName,
  ENABLE_LANDINGPAGE_MANAGER: FeatureFlagName,
  LANDINGPAGE_MANAGER_MODULE_URL: FeatureFlagName,
  LANDINGPAGE_MANAGER_CSS_URL: FeatureFlagName,
}

export const FEATURE_FLAGS: FEATURE_FLAGS_PROPS = {
  ANALYTICS_ENABLE_INTERACTION_SUMMARIZATION: 'analytics.enable-interaction-summarization',
  ANALYTICS_ENABLE_ADVANCED_SENTIMENT: 'analytics.enable-advanced-sentiment',
  ANALYTICS_ENABLE_SMART_TAG: 'analytics.enable-smart-tag',
  ANALYTICS_DISABLE_SENTIMENT: 'analytics.disable-sentiment',
  ANALYTICS_DISABLE_BULK_CONTACT_EXPORT:
    'analytics.disable-transcription-only-export',
  ANALYTICS_DISABLE_PHRASE_MANAGER_SUGGESTIONS: 'analytics.ng-phrase-manager.suggestions.disable',
  ANALYTICS_ALLOW_PHRASE_IN_MULTIPLE_LANGUAGES: 'analytics.allow-phrase-in-multiple-languages',
  ANALYTICS_ENABLE_TRANSCRIPTION_SEARCH: 'analytics.enable-transcription-search',
  IAM_INTEGRATION: 'iam.integration',
  IAM_FORGE_ROCK_USER_SYNC: 'iam.forgerockUserSync',
  IAM_ALLOW_MFA_CONFIGURATION: 'iam.allowMfaConfiguration',
  CDM_DATA_LIBRARY: 'yvrfe.data-library',
  CDM_DATA_TASK_MANAGEMENT: 'yvrfe.data-task-management',
  CDM_DATA_CONNECTOR_MANAGEMENT: 'yvrfe.udm-admin',
  CDM_APP_SETTINGS: 'yvrfe.settings',
  CDM_SHARING_GROUP_ADMIN: 'yvrfe.sharing-groups-admin',
  GENESYS_CONFIG: 'arbys.genesysConfig',
  FEDRAMP_LOGIN_BANNER: 'cher.fedramp-login-banner',
  CDM_USER_ADMIN: 'yvrfe.CDMUserAdmin',
  BI_QS_EMBEDDING: 'bi.yeti.embedding.enabled',
  BI_QS_EMBEDDING_LIB_URL: 'bi.yeti.embedding.clientapp.url',
  BI_QS_EMBEDDING_AUTH_URL: 'bi.yeti.embedding.auth.url',
  BI_QS_EMBEDDING_BACKEND_URL: 'bi.yeti.embedding.backend.url',
  BI_QS_DASHBOARDS_URL: 'bi.yeti.backend.url.dashboards',
  QM_BULK_CONTACT_OPERATIONS_UPDATE_METADATA: 'qm.tenant.enableBulkContactOperationsUpdateMetadata',
  QM_MULTIEVAL_FORM: 'qm.tenant.enableMultipleEvalForms',
  QM_CONTACT_GOAL_ADMIN: 'qm-contact-goal-config-enabled',
  QM_CONTACT_GOALS_QUEUE: 'qm-contact-goal-queue-enabled',
  USE_SYSTEM_METADATA_FIELDS: 'qm.tenant.useSystemMetadataFields',
  ENABLE_SHARED_MEDIA_DIRECTORY: 'qm.tenant.sharedMediaDirectory',
  USE_LANDINGPAGE_SERVICE: 'cone.landingpage.useLandingPageService',
  LANDINGPAGE_SERVICE_PATH: 'cone.landingpage.servicePath',
  LANDINGPAGE_TIMEOUT: 'cone.landingpage.timeout',
  ENABLE_LANDINGPAGE_MANAGER: 'cone.landingpage.enableManagement',
  LANDINGPAGE_MANAGER_MODULE_URL: 'cone.landingpage.managementModuleUrl',
  LANDINGPAGE_MANAGER_CSS_URL: 'cone.landingpage.managementCssUrl',
} as const

export const PAGE_FEATURE_FLAGS_MAP: ReadonlyMap<string, keyof FeatureFlagTypeMap> = new Map([
  [ROUTE_PATHS_PREFIX.EVALUATION_FORM_GROUPS, FEATURE_FLAGS.QM_MULTIEVAL_FORM],
  [ROUTE_PATHS_PREFIX.GENESYS_CONFIGURATION, FEATURE_FLAGS.GENESYS_CONFIG],
  [ROUTE_PATHS_PREFIX.IAM_INTEGRATION, FEATURE_FLAGS.IAM_INTEGRATION],
  [ROUTE_PATHS_PREFIX.DATA_TASK_MANAGEMENT, FEATURE_FLAGS.CDM_DATA_TASK_MANAGEMENT],
  [ROUTE_PATHS_PREFIX.USER_ATTRIBUTES_MANAGEMENT, FEATURE_FLAGS.CDM_USER_ADMIN],
  [ROUTE_PATHS_PREFIX.UDM_CONNECTOR_MANAGEMENT, FEATURE_FLAGS.CDM_DATA_CONNECTOR_MANAGEMENT],
  [ROUTE_PATHS_PREFIX.RT_CONNECTOR_MANAGEMENT, FEATURE_FLAGS.CDM_DATA_CONNECTOR_MANAGEMENT],
  [ROUTE_PATHS_PREFIX.AR_ASSET_BROWSER, FEATURE_FLAGS.CDM_DATA_LIBRARY],
  [ROUTE_PATHS_PREFIX.CDM_SETTINGS, FEATURE_FLAGS.CDM_APP_SETTINGS],
  [ROUTE_PATHS_PREFIX.CDM_SHARING_GROUP_ADMIN, FEATURE_FLAGS.CDM_SHARING_GROUP_ADMIN],
  [ROUTE_PATHS_PREFIX.AR_DATA_LIBRARY, FEATURE_FLAGS.CDM_DATA_LIBRARY],
  [ROUTE_PATHS_PREFIX.BI_INSIGHTS, FEATURE_FLAGS.BI_QS_EMBEDDING],
  [ROUTE_PATHS_PREFIX.LANDINGPAGE_MANAGEMENT, FEATURE_FLAGS.ENABLE_LANDINGPAGE_MANAGER],
])

export const FEATURE_FLAG_DEFAULTS: ReadonlyMap<FeatureFlagName, null | string | number | boolean > = new Map([
  [FEATURE_FLAGS.BI_QS_EMBEDDING, true],
])
